import { z } from 'zod'

export const productSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(3, 'Product name must be at least 3 characters')
      .max(150, 'Product name cannot exceed 150 characters'),

    description: z
      .string()
      .trim()
      .min(10, 'Description must be at least 10 characters')
      .max(5000, 'Description cannot exceed 5000 characters'),

    shortDescription: z
      .string()
      .trim()
      .max(500, 'Short description cannot exceed 500 characters')
      .optional()
      .or(z.literal('')),

    categoryId: z
      .string()
      .min(1, 'Please select a category'),

    tag: z
      .enum(['NEW', 'BEST_SELLER', 'FEATURED', 'TRENDING', 'DISCOUNT', ''])
      .optional()
      .or(z.literal('')),

    sku: z
      .string()
      .trim()
      .max(50, 'SKU cannot exceed 50 characters')
      .optional()
      .or(z.literal('')),

    price: z
      .union([z.coerce.number(), z.string()])
      .refine(
        (val) => {
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num > 0
        },
        'Price must be greater than 0'
      ),

    discountPrice: z
      .union([z.coerce.number(), z.string(), z.literal('')])
      .optional()
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return true
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num > 0
        },
        'Discount price must be greater than 0'
      ),

    lowStockThreshold: z
      .union([z.coerce.number(), z.string(), z.literal('')])
      .optional()
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return true
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num >= 0
        },
        'Low stock threshold must be 0 or greater'
      ),

    stock: z
      .union([z.coerce.number(), z.string()])
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return false
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num >= 0
        },
        'Stock is required and must be at least 0'
      ),

    mainImage: z
      .union([z.instanceof(File), z.string()])
      .optional(),

    isFeatured: z.boolean().optional(),

    metaTitle: z
      .string()
      .trim()
      .max(60, 'Meta title cannot exceed 60 characters')
      .optional()
      .or(z.literal('')),

    metaDescription: z
      .string()
      .trim()
      .max(160, 'Meta description cannot exceed 160 characters')
      .optional()
      .or(z.literal('')),

    metaKeywords: z
      .string()
      .trim()
      .max(200, 'Meta keywords cannot exceed 200 characters')
      .optional()
      .or(z.literal('')),

    weight: z
      .union([z.coerce.number(), z.string(), z.literal('')])
      .optional()
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return true
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num >= 0
        },
        'Weight must be 0 or greater'
      ),

    length: z
      .union([z.coerce.number(), z.string(), z.literal('')])
      .optional()
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return true
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num >= 0
        },
        'Length must be 0 or greater'
      ),

    width: z
      .union([z.coerce.number(), z.string(), z.literal('')])
      .optional()
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return true
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num >= 0
        },
        'Width must be 0 or greater'
      ),

    height: z
      .union([z.coerce.number(), z.string(), z.literal('')])
      .optional()
      .refine(
        (val) => {
          if (val === '' || val === undefined || val === null) return true
          const num = typeof val === 'string' ? parseFloat(val) : val
          return !isNaN(num) && num >= 0
        },
        'Height must be 0 or greater'
      ),
  })
  .refine(
    (data) => {
      if (!data.discountPrice) return true
      const discount = typeof data.discountPrice === 'string' ? parseFloat(data.discountPrice) : data.discountPrice
      const price = typeof data.price === 'string' ? parseFloat(data.price) : data.price
      return discount < price
    },
    {
      path: ['discountPrice'],
      message: 'Discount price must be less than actual price',
    }
  )

export type ProductFormData = z.infer<typeof productSchema>
